/**
 * Blocos isométricos 8×8 assados. Terreno + estáticos de chão, com z.
 * Cache LRU por mapa. Canvas vem de um pool: despejar devolve o buffer,
 * não cria outro. Vizinhos assam em requestIdleCallback, uma vez por
 * célula de câmera — não por bloco visível a cada quadro.
 */

import type { MapaCarregado } from './mapa';

export const BLOCO = 8;

function wx(tx: number, ty: number, tw: number): number {
  return (tx - ty) * tw / 2;
}
function wy(tx: number, ty: number, th: number): number {
  return (tx + ty) * th / 2;
}

export const Z_PX = 4;
/** Tela zoom 1 em 1920×1080 + vizinhos; o cemitério 64×64 cabe inteiro (64). */
export const LRU_MAX = 96;
/** Folga para o z (4 px por unidade) não recortar o losango nem vazar no vizinho. */
const PAD_TOP = 128;
const PAD_BOT = 80;
const LADO = BLOCO * 44; // 352
const ORIGEM_X = LADO / 2; // 176
const ALTURA = LADO + PAD_TOP + PAD_BOT;
const POOL_MAX = LRU_MAX;

/**
 * A folga do bloco. Nos mapas de sempre é a fixa (128 em cima, 80 embaixo). No mapa com relevo (techspec-relevo) o
 * terreno vai do z mínimo ao máximo do mapa, e a folga cresce para o morro não ser recortado.
 */
function padTop(mapa: MapaCarregado): number {
  return mapa.relevo ? Math.max(PAD_TOP, mapa.relevo.zMax * Z_PX + 64) : PAD_TOP;
}

/**
 * O retângulo do bloco assado, medido do vértice de cima do losango do bloco. `topo`/`alt` é o canvas (terreno e chão);
 * `cullTopo`/`cullAlt` também cobre os altos do bloco, para o recorte da tela não perder a parede que sobe para dentro
 * dela.
 */
interface Limites { readonly topo: number; readonly alt: number; readonly cullTopo: number; readonly cullAlt: number }
const FIXO: Limites = { topo: PAD_TOP, alt: ALTURA, cullTopo: PAD_TOP, cullAlt: ALTURA };
const limitesPorMapa = new Map<string, Map<number, Limites>>();

/**
 * No mapa com relevo, a folga é a do próprio bloco, não a do mapa inteiro (Renato, 04/11/2026: queda de fps e travada
 * no Fire e no Ice Dungeon). Com a folga do mapa (z de -29 a 100 no Fire), todo bloco virava um canvas de 352×1012 px,
 * quase todo transparente: a tela desenhava ~13× a própria área por quadro, e a lista de visíveis passava do LRU e
 * reassava blocos a cada quadro. Aqui cada bloco mede o z dos seus cantos, do chão e dos altos.
 */
function limites(mapa: MapaCarregado, bx: number, by: number): Limites {
  const rel = mapa.relevo;
  if (!rel) return FIXO;
  let m = limitesPorMapa.get(mapa.id);
  if (!m) { m = new Map(); limitesPorMapa.set(mapa.id, m); }
  const k = by * 100000 + bx;
  const hit = m.get(k);
  if (hit) return hit;
  const TH = mapa.tileH, MEIO = TH / 2;
  const x0 = bx * BLOCO, y0 = by * BLOCO;
  let cima = 0, baixo = LADO;
  for (let ly = 0; ly < BLOCO; ly++) for (let lx = 0; lx < BLOCO; lx++) {
    const tx = x0 + lx, ty = y0 + ly;
    if (!mapa.dentro(tx, ty)) continue;
    const base = (lx + ly) * MEIO;
    const zt = rel.zCanto(tx, ty), zr = rel.zCanto(tx + 1, ty), zb = rel.zCanto(tx + 1, ty + 1), zl = rel.zCanto(tx, ty + 1);
    cima = Math.min(cima, base - zt * Z_PX, base + MEIO - zr * Z_PX, base + TH - zb * Z_PX, base + MEIO - zl * Z_PX);
    baixo = Math.max(baixo, base - zt * Z_PX, base + MEIO - zr * Z_PX, base + TH - zb * Z_PX, base + MEIO - zl * Z_PX);
  }
  const extremos = (lista: readonly { x: number; y: number; z: number; tile: number }[]) => {
    let a = Infinity, b = -Infinity;
    for (const e of lista) {
      const t = mapa.estaticosTs.tile(e.tile);
      const y = (e.x - x0 + e.y - y0) * MEIO + TH - e.z * Z_PX - t.ancoraY;
      a = Math.min(a, y); b = Math.max(b, y + t.h);
    }
    return [a, b] as const;
  };
  const [ca, cb] = extremos(mapa.chaoNoBloco(bx, by));
  cima = Math.min(cima, ca); baixo = Math.max(baixo, cb);
  const topo = Math.ceil(-cima) + 8;
  const alt = topo + Math.ceil(baixo) + 8;
  const [aa, ab] = extremos(mapa.altosNoBloco(bx, by));
  const cullTopo = Math.max(topo, Math.ceil(-aa) + 8);
  const cullAlt = Math.max(alt - topo, Math.ceil(ab) + 8) + cullTopo;
  const l: Limites = { topo, alt, cullTopo, cullAlt };
  m.set(k, l);
  return l;
}

type Chave = string;
const caches = new Map<string, Map<Chave, HTMLCanvasElement>>();
const pool: HTMLCanvasElement[] = [];
let ultimoAquecido = '';

function cacheDe(mapaId: string): Map<Chave, HTMLCanvasElement> {
  let c = caches.get(mapaId);
  if (!c) {
    c = new Map();
    caches.set(mapaId, c);
  }
  return c;
}

function pegarCanvas(altura = ALTURA): HTMLCanvasElement {
  const cv = pool.pop();
  if (cv) {
    if (cv.height !== altura) cv.height = altura;
    return cv;
  }
  const novo = document.createElement('canvas');
  novo.width = LADO;
  novo.height = altura;
  return novo;
}

function devolverCanvas(cv: HTMLCanvasElement): void {
  if (pool.length >= POOL_MAX) return;
  pool.push(cv);
}

function promover(c: Map<Chave, HTMLCanvasElement>, k: Chave, canvas: HTMLCanvasElement): void {
  if (c.has(k)) c.delete(k);
  c.set(k, canvas);
}

/** O teto do LRU: nunca abaixo do que a tela e a borda aquecida pedem, senão o cache gira e reassa todo quadro. */
let limiteLru = LRU_MAX;
function aparar(c: Map<Chave, HTMLCanvasElement>, preservar: Set<Chave>): void {
  if (c.size <= limiteLru) return;
  for (const k of [...c.keys()]) {
    if (c.size <= limiteLru) break;
    if (preservar.has(k)) continue;
    const cv = c.get(k);
    c.delete(k);
    if (cv) devolverCanvas(cv);
  }
}

export function invalidarMapa(mapaId: string): void {
  const c = caches.get(mapaId);
  if (c) {
    for (const cv of c.values()) devolverCanvas(cv);
    caches.delete(mapaId);
  }
  limitesPorMapa.delete(mapaId);
  if (mapaDaFila?.id === mapaId) { mapaDaFila = null; fila.length = 0; }
  if (ultimoAquecido.startsWith(`${mapaId}:`)) ultimoAquecido = '';
}

export function bytesCache(mapaId?: string): number {
  const bytesDe = (c: Map<Chave, HTMLCanvasElement>) => {
    let n = 0;
    for (const cv of c.values()) n += cv.width * cv.height * 4;
    return n;
  };
  if (mapaId) return bytesDe(cacheDe(mapaId));
  let n = 0;
  for (const c of caches.values()) n += bytesDe(c);
  return n;
}

function chave(bx: number, by: number): Chave {
  return `${bx}:${by}`;
}

function pintarBloco(
  ctx: CanvasRenderingContext2D,
  mapa: MapaCarregado,
  bx: number,
  by: number
): void {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  ctx.clearRect(0, 0, LADO, ctx.canvas.height);
  ctx.imageSmoothingEnabled = false;
  const rel = mapa.relevo;
  const topo = limites(mapa, bx, by).topo;

  const x0 = bx * BLOCO;
  const y0 = by * BLOCO;
  const TW = mapa.tileW, TH = mapa.tileH;
  const img = mapa.terrenos.imagem;

  const casas: Array<{ lx: number; ly: number; z: number }> = [];
  for (let ly = 0; ly < BLOCO; ly++) {
    for (let lx = 0; lx < BLOCO; lx++) {
      const tx = x0 + lx, ty = y0 + ly;
      if (!mapa.dentro(tx, ty)) continue;
      // No relevo o terreno é desenhado pelo canto de cima (o z do terreno), não pelo pé.
      casas.push({ lx, ly, z: rel ? rel.zCanto(tx, ty) : mapa.zEm(tx, ty) });
    }
  }
  casas.sort((a, b) => (a.lx + a.ly) - (b.lx + b.ly) || a.lx - b.lx || a.z - b.z);

  // Relevo: as casas vão direto nos pixels do bloco, numa passada só (sem um canvas por casa).
  const pixels = rel ? ctx.createImageData(LADO, ctx.canvas.height) : null;
  for (const { lx, ly, z } of casas) {
    const tx = x0 + lx, ty = y0 + ly;
    const dx = ORIGEM_X + (lx - ly) * (TW / 2) - TW / 2;
    const dy = topo + (lx + ly) * (TH / 2) - z * Z_PX;
    if (rel && pixels) {
      rel.pintarCasa(tx, ty, pixels.data, pixels.width, pixels.height, dx, dy);
      continue;
    }
    const t = mapa.terrenos.tile(mapa.tileEm(tx, ty));
    ctx.drawImage(img, t.x, t.y, t.w, t.h, dx, dy, t.w, t.h);
  }
  if (pixels) ctx.putImageData(pixels, 0, 0);

  const chaoDoBloco = mapa.chaoNoBloco(bx, by);
  for (const e of chaoDoBloco) {
    const t = mapa.estaticosTs.tile(e.tile);
    const lx = e.x - x0, ly = e.y - y0;
    const cx = ORIGEM_X + (lx - ly) * (TW / 2);
    // Mesma âncora do desenho.ts: vértice de baixo do losango, não o centro.
    const cy = topo + (lx + ly) * (TH / 2) + TH - e.z * Z_PX;
    ctx.drawImage(img, t.x, t.y, t.w, t.h, cx - t.ancoraX, cy - t.ancoraY, t.w, t.h);
  }
}

/** Assa um bloco (ou devolve o do cache). Quem chama de fora entra no LRU. */
export function assarBloco(mapa: MapaCarregado, bx: number, by: number): HTMLCanvasElement {
  const cv = obter(mapa, bx, by);
  if (!cv) throw new Error('Bloco: sem contexto 2D.');
  return cv;
}

function obter(mapa: MapaCarregado, bx: number, by: number): HTMLCanvasElement | null {
  const c = cacheDe(mapa.id);
  const k = chave(bx, by);
  const hit = c.get(k);
  if (hit) {
    promover(c, k, hit);
    return hit;
  }
  const cv = pegarCanvas(limites(mapa, bx, by).alt);
  const ctx = cv.getContext('2d');
  if (!ctx) {
    devolverCanvas(cv);
    return null;
  }
  try {
    pintarBloco(ctx, mapa, bx, by);
  } catch (err) {
    console.warn(`Bloco ${mapa.id}:${bx}:${by}: falha ao assar.`, err);
    devolverCanvas(cv);
    return null;
  }
  promover(c, k, cv);
  return cv;
}

const ric: (cb: () => void) => void =
  typeof requestIdleCallback === 'function'
    ? (cb) => { requestIdleCallback(() => cb()); }
    : (cb) => { setTimeout(cb, 16); };

const aquecendo = new Set<string>();

/**
 * A borda da tela assada antes de entrar nela: no relevo assar é caro (cada casa é um canvas próprio), e o bloco que
 * aparecia na borda era assado no meio do quadro — a travadinha andando. Fila com prazo do requestIdleCallback.
 */
const fila: Array<{ bx: number; by: number }> = [];
let mapaDaFila: MapaCarregado | null = null;
let filaAgendada = false;
function agendarFila(): void {
  if (filaAgendada) return;
  filaAgendada = true;
  const rodar = (prazo: () => number) => {
    filaAgendada = false;
    const mapa = mapaDaFila;
    if (!mapa) return;
    let feitos = 0;
    while (fila.length && (feitos === 0 || prazo() > 3)) {
      const { bx, by } = fila.shift()!;
      if (cacheDe(mapa.id).has(chave(bx, by))) continue;
      obter(mapa, bx, by);
      feitos++;
    }
    if (fila.length) agendarFila();
  };
  if (typeof requestIdleCallback === 'function')
    requestIdleCallback(d => rodar(() => d.timeRemaining()), { timeout: 250 });
  else {
    const fim = performance.now() + 8;
    setTimeout(() => rodar(() => fim - performance.now()), 16);
  }
}

function aquecerBorda(mapa: MapaCarregado, cam: { x: number; y: number }, vw: number, vh: number, zoom: number): void {
  if (mapaDaFila !== mapa) { mapaDaFila = mapa; fila.length = 0; }
  const folga = LADO * zoom * 1.5;
  const c = cacheDe(mapa.id);
  const vistos = new Set(fila.map(f => chave(f.bx, f.by)));
  for (const b of blocosVisiveis(mapa, cam, vw + folga * 2, vh + folga * 2, zoom)) {
    const k = chave(b.bx, b.by);
    if (c.has(k) || vistos.has(k)) continue;
    fila.push(b);
    vistos.add(k);
  }
  if (fila.length) agendarFila();
}

export function aquecerVizinhos(mapa: MapaCarregado, bx: number, by: number): void {
  const blocosX = Math.ceil(mapa.largura / BLOCO);
  const blocosY = Math.ceil(mapa.altura / BLOCO);
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const nx = bx + dx, ny = by + dy;
      if (nx < 0 || ny < 0 || nx >= blocosX || ny >= blocosY) continue;
      const id = `${mapa.id}:${nx}:${ny}`;
      if (aquecendo.has(id) || cacheDe(mapa.id).has(chave(nx, ny))) continue;
      aquecendo.add(id);
      ric(() => {
        aquecendo.delete(id);
        if (!cacheDe(mapa.id).has(chave(nx, ny))) obter(mapa, nx, ny);
      });
    }
  }
}

function blocoDaCamera(mapa: MapaCarregado, cam: { x: number; y: number }): { bx: number; by: number } {
  const TW = mapa.tileW, TH = mapa.tileH;
  const cxT = cam.y / TH + cam.x / TW;
  const cyT = cam.y / TH - cam.x / TW;
  return { bx: Math.floor(cxT / BLOCO), by: Math.floor(cyT / BLOCO) };
}

export function aquecerSpawn(mapa: MapaCarregado): void {
  aquecerEm(mapa, mapa.spawn.x, mapa.spawn.y);
}

/** Assa o bloco do tile e os vizinhos — o spawn do arquivo não é o destino do portal. */
export function aquecerEm(mapa: MapaCarregado, x: number, y: number): void {
  const bx = Math.floor(x / BLOCO);
  const by = Math.floor(y / BLOCO);
  obter(mapa, bx, by);
  aquecerVizinhos(mapa, bx, by);
}

export function blocosVisiveis(
  mapa: MapaCarregado,
  cam: { x: number; y: number },
  vw: number,
  vh: number,
  zoom: number
): Array<{ bx: number; by: number }> {
  const TW = mapa.tileW, TH = mapa.tileH;
  // Na grade isométrica os dois eixos andam na diagonal da tela: a meia-largura e a meia-altura somam nos dois.
  // (Antes o eixo y só via a altura: em tela larga o canto de baixo à esquerda ficava sem chão.) A folga cobre o
  // estático alto que nasce abaixo da tela e sobe para dentro dela (PAD_TOP).
  const meio = ((vw / zoom) / (2 * TW) + (vh / zoom) / (2 * TH));
  const abaixo = Math.max(padTop(mapa), mapa.relevo ? mapa.relevo.zMax * Z_PX + 200 : 0) / (TH / 2);
  const cxT = cam.y / TH + cam.x / TW;
  const cyT = cam.y / TH - cam.x / TW;
  const x0 = Math.max(0, Math.floor((cxT - meio - 4) / BLOCO));
  const y0 = Math.max(0, Math.floor((cyT - meio - 4) / BLOCO));
  const x1 = Math.min(Math.ceil(mapa.largura / BLOCO) - 1, Math.floor((cxT + meio + abaixo) / BLOCO));
  const y1 = Math.min(Math.ceil(mapa.altura / BLOCO) - 1, Math.floor((cyT + meio + abaixo) / BLOCO));
  // O recorte em casas é um losango maior que a tela: fica só o bloco cujo retângulo assado (com o PAD de cima e de
  // baixo) cruza a tela. Assim a tela larga pede ~60 blocos, não 100, e o cache (LRU_MAX) não fica girando.
  const out: Array<{ bx: number; by: number }> = [];
  for (let by = y0; by <= y1; by++)
    for (let bx = x0; bx <= x1; bx++) {
      const tx = bx * BLOCO, ty = by * BLOCO;
      const { cullTopo, cullAlt } = limites(mapa, bx, by);
      const sx = ((tx - ty) * TW / 2 - cam.x) * zoom + vw / 2 - ORIGEM_X * zoom;
      const sy = ((tx + ty) * TH / 2 - cam.y) * zoom + vh / 2 - cullTopo * zoom;
      if (sx > vw || sy > vh || sx + LADO * zoom < 0 || sy + cullAlt * zoom < 0) continue;
      out.push({ bx, by });
    }
  return out;
}

export function desenharBlocos(
  ctx: CanvasRenderingContext2D,
  mapa: MapaCarregado,
  cam: { x: number; y: number },
  vw: number,
  vh: number,
  zoom: number
): void {
  const vis = blocosVisiveis(mapa, cam, vw, vh, zoom)
    .slice()
    .sort((a, b) => (a.bx + a.by) - (b.bx + b.by) || a.bx - b.bx);
  ctx.imageSmoothingEnabled = false;
  const TW = mapa.tileW, TH = mapa.tileH;
  const visChaves = new Set<Chave>();
  for (const { bx, by } of vis) {
    visChaves.add(chave(bx, by));
    const baked = obter(mapa, bx, by);
    if (!baked) continue;
    const tx = bx * BLOCO, ty = by * BLOCO;
    const sx = (wx(tx, ty, TW) - cam.x) * zoom + vw / 2 - ORIGEM_X * zoom;
    const sy = (wy(tx, ty, TH) - cam.y) * zoom + vh / 2 - limites(mapa, bx, by).topo * zoom;
    ctx.drawImage(baked, sx, sy, LADO * zoom, baked.height * zoom);
  }
  limiteLru = Math.max(LRU_MAX, Math.ceil(vis.length * 1.8) + 16);
  aparar(cacheDe(mapa.id), visChaves);
  const centro = blocoDaCamera(mapa, cam);
  const marca = `${mapa.id}:${centro.bx}:${centro.by}`;
  if (marca !== ultimoAquecido) {
    ultimoAquecido = marca;
    // No relevo, a borda inteira da tela; nos outros mapas o assar é barato e os vizinhos da câmera bastam.
    if (mapa.relevo) aquecerBorda(mapa, cam, vw, vh, zoom);
    else aquecerVizinhos(mapa, centro.bx, centro.by);
  }
}
